// API Configuration
// Uses environment variables with fallback to localhost for development

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001";

export const API_ENDPOINTS = {
  login: `${API_BASE_URL}/login`,
  register: `${API_BASE_URL}/register`,
  logout: `${API_BASE_URL}/logout`,
  leagues: `${API_BASE_URL}/leagues`,
  // Note: seasons, teams, and games don't have standalone list endpoints
  // They are nested under their parent resources (leagues/seasons)
  // Use the helper functions below: getSeasonsUrl(), getTeamsUrl(), getGamesUrl(), etc.
};

// Helper to build API URLs with path parameters
export function buildApiUrl(
  endpoint: string,
  params?: Record<string, string | number>,
): string {
  let url = endpoint;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, String(value));
    });
  }
  return url;
}

/**
 * Backend API Routes Reference
 * ============================
 *
 * Authentication:
 * - POST   /login                                    - User login
 * - POST   /register                                 - User registration
 * - DELETE /logout                                   - User logout
 *
 * Leagues:
 * - GET    /leagues                                  - List user's leagues
 * - POST   /leagues                                  - Create league
 * - GET    /leagues/:id                              - Get league details
 * - PATCH  /leagues/:id                              - Update league
 * - DELETE /leagues/:id                              - Delete league
 *
 * Seasons (nested under leagues):
 * - GET    /leagues/:league_id/seasons               - List league's seasons
 * - POST   /leagues/:league_id/seasons               - Create season
 * - GET    /seasons/:id                              - Get season details
 * - PATCH  /seasons/:id                              - Update season
 * - DELETE /seasons/:id                              - Delete season
 *
 * Teams (nested under seasons):
 * - GET    /seasons/:season_id/teams                 - List season's teams
 * - POST   /seasons/:season_id/teams                 - Create team
 * - GET    /teams/:id                                - Get team details
 * - PATCH  /teams/:id                                - Update team
 * - DELETE /teams/:id                                - Delete team
 *
 * Players (nested under leagues):
 * - GET    /leagues/:league_id/players               - List league's players
 * - POST   /leagues/:league_id/players               - Create player
 * - GET    /players/:id                              - Get player details
 * - PATCH  /players/:id                              - Update player
 * - DELETE /players/:id                              - Delete player
 *
 * Team Players:
 * - GET    /teams/:team_id/players                   - List team's players
 * - POST   /teams/:team_id/players                   - Add player to team
 * - PATCH  /team_players/:id                         - Update team player
 * - DELETE /team_players/:id                         - Remove player from team
 *
 * Team Games:
 * - GET    /teams/:team_id/games                     - List team's games (home and away)
 * - GET    /teams/:team_id/calendar.ics              - Public iCalendar feed (no auth or App-League)
 *
 * Games (nested under seasons):
 * - GET    /seasons/:season_id/games                 - List season's games
 * - POST   /seasons/:season_id/games                 - Create game
 * - GET    /games/:id                                - Get game details
 * - PATCH  /games/:id                                - Update game
 * - DELETE /games/:id                                - Delete game
 *
 * Goals, Penalties, Spares (nested under games):
 * - GET/POST /games/:game_id/goals                   - Game goals
 * - GET/POST /games/:game_id/penalties               - Game penalties
 * - GET/POST /games/:game_id/spares                  - Game spares
 *
 * League Users:
 * - GET    /leagues/:league_id/users                 - List league users
 * - POST   /leagues/:league_id/users                 - Add user to league (by email)
 * - GET    /leagues/:league_id/users/me              - Current user's membership
 * - PATCH  /league_users/:id                         - Update league user role
 * - DELETE /league_users/:id                         - Remove user from league
 *
 * Stats:
 * - GET    /seasons/:season_id/team_stats            - Season team statistics
 *
 * Reference data (public, no App-League header):
 * - GET    /positions                                - Player positions
 * - GET    /penalty_types                            - Penalty types
 * - GET    /time_zones                               - Timezones a league can use
 *
 * IMPORTANT: Most endpoints require the App-League header with league abbreviation
 */

// League-specific endpoints
export function getLeagueUrl(leagueId: number): string {
  return `${API_BASE_URL}/leagues/${leagueId}`;
}

export function getLeagueByAbbrUrl(abbr: string): string {
  return `${API_BASE_URL}/leagues/${abbr}`;
}

export function getSeasonsUrl(leagueId: number): string {
  return `${API_BASE_URL}/leagues/${leagueId}/seasons?sort=-name`;
}

export function getSeasonUrl(seasonId: number): string {
  return `${API_BASE_URL}/seasons/${seasonId}`;
}

export function getTeamsUrl(seasonId: number): string {
  return `${API_BASE_URL}/seasons/${seasonId}/teams`;
}

export function getTeamUrl(teamId: number): string {
  return `${API_BASE_URL}/teams/${teamId}`;
}

export function getPlayersUrl(leagueId: number): string {
  return `${API_BASE_URL}/leagues/${leagueId}/players`;
}

export function getPlayerUrl(playerId: number): string {
  return `${API_BASE_URL}/players/${playerId}`;
}

export function getTeamPlayersUrl(teamId: number): string {
  return `${API_BASE_URL}/teams/${teamId}/players`;
}

export function getGamesUrl(seasonId: number): string {
  return `${API_BASE_URL}/seasons/${seasonId}/games`;
}

/**
 * Public iCalendar feed of a team's games. Calendar apps subscribe to this URL
 * directly, so it needs no auth or App-League header.
 */
/** Reference list of timezones a league can be set to. Public, no auth. */
export function getTimeZonesUrl(): string {
  return `${API_BASE_URL}/time_zones`;
}

export function getTeamCalendarUrl(teamId: number): string {
  return `${API_BASE_URL}/teams/${teamId}/calendar.ics`;
}

export function getGameUrl(gameId: number): string {
  return `${API_BASE_URL}/games/${gameId}`;
}

export function getLeagueUsersUrl(leagueId: number): string {
  return `${API_BASE_URL}/leagues/${leagueId}/users`;
}

export function getCurrentLeagueUserUrl(leagueId: number): string {
  return `${API_BASE_URL}/leagues/${leagueId}/users/me`;
}

export function getLeagueUserUrl(leagueUserId: number): string {
  return `${API_BASE_URL}/league_users/${leagueUserId}`;
}

export function getSeasonStatsUrl(
  seasonId: number,
  gameType?: "regular" | "exhibition" | "playoff",
): string {
  const baseUrl = `${API_BASE_URL}/seasons/${seasonId}/team_stats`;
  if (gameType) {
    return `${baseUrl}?game_type=${gameType}`;
  }
  return baseUrl;
}

export function getSeasonPlayerStatsUrl(
  seasonId: number,
  gameType?: "regular" | "exhibition" | "playoff",
  statType?: "skater" | "goalie",
): string {
  const baseUrl = `${API_BASE_URL}/seasons/${seasonId}/player_stats`;
  const params = new URLSearchParams();
  if (gameType) {
    params.append("game_type", gameType);
  }
  if (statType) {
    params.append("stat_type", statType);
  }
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function getTeamPlayerStatsUrl(
  teamId: number,
  gameType?: "regular" | "exhibition" | "playoff",
  statType?: "skater" | "goalie",
): string {
  const baseUrl = `${API_BASE_URL}/teams/${teamId}/player_stats`;
  const params = new URLSearchParams();
  if (gameType) {
    params.append("game_type", gameType);
  }
  if (statType) {
    params.append("stat_type", statType);
  }
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function getPlayerStatsUrl(
  playerId: number,
  statType?: "skater" | "goalie",
): string {
  const baseUrl = `${API_BASE_URL}/players/${playerId}/stats`;
  if (statType) {
    return `${baseUrl}?stat_type=${statType}`;
  }
  return baseUrl;
}

export function getActivateSeasonUrl(seasonId: number): string {
  return `${API_BASE_URL}/seasons/${seasonId}/activate`;
}

export function getDeactivateSeasonUrl(seasonId: number): string {
  return `${API_BASE_URL}/seasons/${seasonId}/activate`;
}

// Game events endpoints
export function getGoalsUrl(gameId: number): string {
  return `${API_BASE_URL}/games/${gameId}/goals`;
}

export function getGoalUrl(goalId: number): string {
  return `${API_BASE_URL}/goals/${goalId}`;
}

export function getPenaltiesUrl(gameId: number): string {
  return `${API_BASE_URL}/games/${gameId}/penalties`;
}

export function getPenaltyUrl(penaltyId: number): string {
  return `${API_BASE_URL}/penalties/${penaltyId}`;
}

export function getSparesUrl(gameId: number): string {
  return `${API_BASE_URL}/games/${gameId}/spares`;
}

export function getSpareUrl(spareId: number): string {
  return `${API_BASE_URL}/spares/${spareId}`;
}

export function getGamePlayerStatsUrl(
  gameId: number,
  statType?: "skater" | "goalie",
): string {
  const baseUrl = `${API_BASE_URL}/games/${gameId}/player_stats`;
  if (statType) {
    return `${baseUrl}?stat_type=${statType}`;
  }
  return baseUrl;
}

// Global lookup endpoints (do not require App-League header)
// Use with useRequest(..., ..., undefined, true) to skip league header
export function getPenaltyTypesUrl(): string {
  return `${API_BASE_URL}/penalty_types`;
}

export function getPositionsUrl(): string {
  return `${API_BASE_URL}/positions`;
}
